// `/checkout` — buying a credit on the website, and spending one. STRIPE TEST MODE (WEB-PAY-1) unless the server's held
// live-payments switch is on (LAUNCH-PAY): the "Test mode" line shows whenever `offers` says `mode: "test"`, which the
// server answers in every state except the switch on with a live key.
//
// NO PRICE STRING EXISTS ANYWHERE IN THIS PACKAGE, AND THAT STAYS TRUE WITH PRICES ON THE SCREEN. The prototype
// hardcoded "$29.99" in three places (WEB_PATHWAY_INVENTORY.md §3 row 28). What this screen prints is what
// `v2CreateCheckout` `offers` answers: each product's price already rendered by the server ("$29.99") and the credits it
// mints — the app's products at the app's prices (Tyler, 2026-09-28: "web price is 29.99.. should match ios version"),
// the returning-owner price only when the server says this account may have it. The client decides none of it, and
// `test/thirtyThreeDecisions.test.tsx` row 28 still finds no price in this package's code.
//
// BUYING LEAVES FOR STRIPE, AND CREDITS COME BACK BY WEBHOOK. `create` answers one URL, Stripe's hosted page, and the
// `Checkout` seam sends the owner there. Stripe sends them back to `/checkout/return`, which brings them straight back
// here (CR-1) with the approved thanks line; nothing on this page adds a credit.
//
// WEB CREDITS ARE THIS WEBSITE'S (Tyler, 2026-09-28: "credits dont follow between platforms at this time"). The
// separate-balances line, the test-mode notice and the return line are held by this client (`heldCopy.ts`, WEB-COPY-1
// items 8, 17 and 18). One account (ACC-3b) makes an Apple account's balance the app's too, so which line says where
// credits work — item 8, one of the two account lines Tyler chose on 2026-10-14 (option B), or none — is
// `balanceLine.ts`'s (B6b).
//
// "WHAT A CREDIT GETS YOU" IS NO LONGER ON THIS PAGE (R251). Tyler, 2026-10-13, on `/checkout`: "i
// like the top part of this page but the bottom gets too long.. we need to encourage people to buy a credit and try it
// rather than scaring them away.. it gets too lengthy and the what a credit gets you kind of doubles down on the top part
// too.. mock up a new version". His words supersede the 2026-09-29 placement (WEB-BENEFITS; the parity audit's D6), which
// kept the card here in place of the app's perks list. Two of its approved lines stay, as small lines under the Buy button
// (`checkout.benefits.neverExpire`, `checkout.benefits.straightAnswer`); the card's table stays in `heldCopy.ts`, unrendered
// otherwise. No word is new.
//
// SO THE PAGE IS SHORT, IN TWO SHAPES BY THE BALANCE (R251):
//   - NO CREDIT: the whole before-you-pay panel, then (after any payment notice) the buy card, whose header carries the
//     balance as a small line. There is no separate balance card, so no "Get credits" and no out-of-credits line: the Buy
//     button is right there. The can-raise notice is one calm line in that card, under the Buy button and its two small
//     lines, before the fine print (Tyler, 2026-10-13: "for raise.. B"); it was a card above the money.
//   - A CREDIT TO SPEND: the panel's compact part (`PrePayPanel` `compact`), the balance and "Use a credit and start". The
//     bundles stay folded; "Get credits" (the app's own words for opening its purchase sheet, AEF:808), a ghost button,
//     unfolds them for an owner who wants more.
//   - WHEREVER THE BUY CARD IS SHOWN, IT IS THE WHOLE CARD, WHATEVER THE BALANCE (R-pending-review-fixes): the test-mode
//     line, the bundles, Buy, the two small lines, the can-raise line and the fine print. The independent review of
//     2026-10-13 found the can-raise line drawn with no credit only, so an owner with a credit who pressed "Get credits"
//     for a Pennsylvania home and bought three more paid with no can-raise notice before the money. The app's purchase
//     sheet shows its can-raise card on every purchase (BuyPacketsSheet.swift `backfireCard`), and so does this card.
//
// WEB-PARITY-2B: THE REST IS THE APP'S PURCHASE AND SPEND, IN THE APP'S ORDER AND WORDS (`components/prePayCopy.ts`):
//   - the purchase sheet's hero (CO-4), its can-raise card as one line (RK-1 to RK-3: the app's title for G-2's card and
//     "You'll be asked to confirm you understand this before we generate anything."), its "Welcome back" card for the
//     returning-owner price (CO-6), and its bundle picker with one Buy button (CO-3) — BuyPacketsSheet.swift;
//   - "Use a credit and start" is drawn only with a credit to use (WALK-FIX-2). Its zero-balance stand-ins, the app's
//     "Get credits" and "You're out of credits. Buy more to unlock PROppeal." (AEF:761-797), are gone with the balance
//     card at zero (R251, above);
//   - "Use a credit and start" opens the app's credit-confirm sheet (CO-1: what one credit covers, the unit, the protest
//     window, "Use my credit" / "Not yet") — AppealEntryFlow.swift:429-603. SINCE R251 IT IS A
//     POP-UP (Tyler, 2026-10-13: "the use your credit confirmation also kind of looks the same as before.. it almost
//     feels like it glitches or something"): a dimmed page, a focused card, Escape as "Not yet". The five cover rows are
//     NOT in it, and an owner who reaches it does not see them anywhere on this page: the pop-up opens only with a
//     credit to spend, and with one the panel above is its compact part, which draws no cover rows either
//     (`compact={hasCredit}`). That is as Tyler asked for the pop-up (R251), not an oversight; its covers line ("One credit
//     covers your entire appeal … — start to finish.") says what the credit is for. M3 is in it, because it says when the
//     credit is used; and it carries a small copy of the panel's letter page, which Tyler asked to keep ("i enjoyed the
//     document on it");
//   - and IN FRONT OF IT, WHEN THE SERVER HOLDS THE SPEND FOR THE ADDRESS (G-3), the app's compass-direction sheet (CO-2,
//     AEF:855-900): "Go back and fix the address", or "Use {address} anyway — I meant this one", which is the one thing
//     that sends `confirmations.address`. Until WEB-PARITY-2B nothing on the website sent it, so an owner the server held
//     here could never get past.
// THE ADDRESS ANSWER ON THIS PAGE is the address op, re-asked with the address this tab sent (as the gates re-ask it), and
// only while the appeal has no credit on it — a paid appeal's address is settled, and the server refuses to re-check it.
// AFTER STRIPE'S FULL-PAGE RETURN the typed address is gone (it is kept in memory only; the tab keeps the appeal id and
// nothing else). R-pending-return-resolution (Tyler, 2026-10-14, "A"): while the server says RESOLVE_RESUME_ENABLED is on
// (`capabilities.resolveResume`, from the `get` this page already reads), the page asks v2ResolveProperty op `resume` once
// for the held appeal — the server's own stored answer, nothing geocoded and nothing written, with the protest window
// worked out again for today (functions/src/v2/resolve/resume.ts) — and the panel, the pop-up (its address, unit and T-1
// notice), the compass sheet, G-1's note and the can-raise line render from it exactly as from the address op's. It waits
// at most the purchase notice's eight seconds, and "Use a credit and start" waits with it, so the pop-up cannot be passed
// before the notice arrives. ANY failure or silence is today's page: no card and no address to state back; the spend
// itself still refuses a held address with the server's own sentence, and the owner is offered "Go back and fix the
// address". The switch off: nothing is asked, and the page is today's, request for request.
//
// R-pending-consent-gate (Tyler, 2026-10-14, "yes and yes.."): an ACCOUNT whose current Terms and Privacy Policy are not on
// file is sent to the terms page and back before anything here is shown (`requireConsent`, passed by `CheckoutGate` for a
// signed-in account with WEB_ACCOUNTS_ENABLED on). THE SERVER SAYS SO, on the `get` this page already reads:
// `legal.consentRequired` (2.30.0), present only while its CHECKOUT_REQUIRES_CURRENT_CONSENT_ENABLED is on and the purchase
// and spend would be refused for the terms — the one function both answer from (`functions/src/v2/credits/
// consentGate.ts`). Not `consentOnFile: false`, which a look-around and a server whose token still reads the caller as one
// answer too, and on which this page would send an owner to agree to terms the server is not asking for. The server's own
// refusal of a purchase or a spend for the same reason (CONSENT_REQUIRED) goes the same way, carrying its sentence to the
// terms page. The switch off, or consent on file: this page exactly as before.
//
// LAUNCH-PF (R244; R182, docs/PURCHASE_FIRST.md): THE OWNER'S OWN RECENT PURCHASE, TOLD BEFORE THE CREDIT.
// While the server says PURCHASE_NOTICE_PRESPEND_ENABLED is on (`capabilities.purchaseNoticePrespend`, from the `get` this
// page already reads), "Use a credit and start" — once the compass sheet has settled the address — asks op
// `purchaseNotice` once for the appeal and address, waiting at most the app's eight seconds, and the app's notice sheet
// (`purchaseNoticeSheet.tsx`) stands in front of the credit-confirm sheet while the server's notice is unanswered: the
// app's order (`PurchaseNoticeGate.nextStep`). "Go back" closes it and spends nothing; each of the three routes goes on to
// the credit sheet, which still asks. The spend itself refuses while a notice stands unanswered (`spendAllowed`). The
// switch off: nothing is asked and the page is today's, step for step (`app/purchaseNotice.ts`).
//
// R-pending-web-free-first (Tyler, 2026-10-14: "A", "Yes on layout"): THE FREE CHECK'S FIRST LOOK, BEFORE THE MONEY.
// While the server says FREE_CHECK_V2_ENABLED is on (`capabilities.freeCheckFirstLook`) and the appeal is unspent, the page
// asks v2ResolveProperty op `freeCheck` once per appeal (memory only, `app/freeFirst.ts`; again after a reload or Stripe's
// return, by the appeal id alone), once the address answer has settled, and draws the server's first look in the panel
// under its top row, in both layouts, with a link to the whole check in a pop-up (`routes/freeFirst.tsx`). While asking it
// holds the band's place; a failure, a refusal or 45 seconds of silence draw nothing. The pop-up's buttons into the paid flow
// close it and do this page's next thing — Buy with no credit, "Use a credit and start" with one — never Stripe. The switch
// off: nothing asked, nothing drawn, the page as it was.
//
// SPENDING IS OTHERWISE UNCHANGED: the four decisions `v2Credits` `spend` returns, each a refusal with its own sentence.
//
// WEB-DESIGN-1 (R234; Tyler's walk, 2026-10-05, decisions 2, 3 = A and 5): the hero is the app's
// credit-confirm sheet laid out as a panel — the home, the cover rows with the app's symbols, M3 and a page of a letter
// (`prePayPanel.tsx`); the can-raise card was calm, before the money (one line in the buy card since Tyler's "for raise..
// B", 2026-10-13); and "What PROppeal is — and isn't" sits by the
// Buy button (`FinePrint`, the assent page's own row), since an owner whose consent the server holds skips that page.

import { useEffect, useId, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import type { CheckoutProduct, Copy, PurchaseNotice, ResolvePropertyResponse, V2Failure, V2Result } from "@contract";
import { useWebAccounts } from "../app/accounts";
import { balanceLine } from "./balanceLine";
import { unitOrNull } from "../app/handIn";
import { useAppealId, useSession } from "../app/session";
import { useAction, useCall } from "../app/useCall";
import { CopyLine, CopyNote, DecisionView, HeldLine } from "../components/copy";
import { heldText } from "../components/heldCopy";
import { Actions } from "../components/layout";
import { prePayTemplate, prePayText } from "../components/prePayCopy";
import { Refusal } from "../components/Refusal";
import { FinePrint } from "./assent";
import { checkoutButtonClasses } from "./checkoutLead";
import { CalendarIcon, CanRaiseLine, LetterIllustration, PrePayPanel } from "./prePayPanel";
import { Icon } from "./steps/parity4Parts";
import {
  PURCHASE_NOTICE_DEADLINE_MS, heldPurchaseAnswer, heldPurchaseNotice, lookUpPurchaseNotice, nextPreSpendSheet,
  recordPurchaseChoice, spendAllowed, within, type PurchaseNoticeChoice,
} from "../app/purchaseNotice";
import { PurchaseNoticeSheet } from "./purchaseNoticeSheet";
import { hashesOf, spendServedTexts, spendSheetTexts } from "../app/shownCopy";
import { APP_VERSION } from "./assent";
import { PopUp } from "./popUp";
import { askFreeCheckOnce, type FreeCheckAnswer } from "../app/freeFirst";
import { FreeCheckPanel, FreeFirstBand } from "./freeFirst";

/** The address a credit would be spent on: the owner's own words when the server kept them, else the match. */
function spentOn(resolution: ResolvePropertyResponse): string {
  const a = resolution.body.address;
  return a.used === "typed" ? a.typed : a.resolved ?? a.typed;
}

/** Whether the server holds the spend until the owner confirms the address (G-3; `spendGate.ts`). */
function holdsForAddress(resolution: ResolvePropertyResponse): boolean {
  const g3 = resolution.decisions["G-3"];
  return g3.ran && g3.value.confirmationRequired;
}

/** A refused spend that the server held for the address, read from the refusal's own decisions. */
function heldForAddress(failure: V2Failure): boolean {
  if (failure.kind !== "v2Error") return false;
  const g3 = failure.error.error.decisions?.["G-3"] as { ran?: boolean; value?: { confirmationRequired?: boolean } } | undefined;
  return g3?.ran === true && g3.value?.confirmationRequired === true;
}

/** "1 credit" / "3 credits": the app's bundle label (BuyPacketsSheet.swift:92), from the credits the server sells. */
function bundleLabel(product: CheckoutProduct): string {
  return product.credits === 1 ? prePayText("purchase.bundle.one") : prePayTemplate("purchase.bundle.many", { quantity: product.credits });
}

/** The sheets in front of the money, in the app's order: the compass (the address), the purchase notice (LAUNCH-PF), then
 *  the credit sheet. */
type Sheet = "none" | "compass" | "purchase" | "confirm";

/** Brings the buy card into view and puts the keyboard on its Buy button (or on the card, while the offers load). */
function bringToBuy(card: HTMLElement | null): void {
  if (card === null) return;
  card.scrollIntoView?.({ behavior: "smooth", block: "center" });
  (card.querySelector<HTMLElement>("[data-buy]") ?? card).focus({ preventScroll: true });
}

/** The two refusals of ACC-1's account gate on `create` (contract 2.16.0), both 403, both only with the server's
 *  WEB_ACCOUNTS_ENABLED on. */
export type AccountGateCode = "ACCOUNT_REQUIRED" | "EMAIL_NOT_VERIFIED";

function accountGateCode(failure: V2Failure): AccountGateCode | null {
  if (failure.kind !== "v2Error") return null;
  const code: string = failure.error.error.code;
  if (code === "ACCOUNT_REQUIRED") return "ACCOUNT_REQUIRED";
  if (code === "EMAIL_NOT_VERIFIED") return "EMAIL_NOT_VERIFIED";
  return null;
}

/** R-pending-consent-gate: the server refused a purchase or a spend because the current Terms and Privacy Policy are not on
 *  file (CONSENT_REQUIRED, contract 2.30.0) — its sentence, to carry to the terms page; else null. */
function consentRefusal(failure: V2Failure): Copy | null {
  if (failure.kind !== "v2Error") return null;
  const code: string = failure.error.error.code;
  return code === "CONSENT_REQUIRED" ? failure.error.error.copy : null;
}

/** Where the terms page is, and what it is told: come back to `/checkout`, and the server's sentence when it refused. The
 *  router's state, never a URL parameter (`account/finish.ts` `accountState`). */
export interface ConsentRoute {
  readonly from: "/checkout";
  readonly consentRefusal?: Copy;
}

/** The address phase's answer as no answer: today's page. */
const NO_ANSWER: V2Result<ResolvePropertyResponse | null> = { ok: true, status: 200, response: null };

/**
 * `onAccountGate` (ACC-3a): with the server's WEB_ACCOUNTS_ENABLED on, `routes/account/CheckoutGate.tsx` passes it, and a
 * `create` the server refused for the account — a look-around, or an unconfirmed email — goes to the gate, whose screen
 * says so, instead of standing here as a refusal. Absent (the switch off, today), nothing changes: the server never
 * sends either code, and if it did the refusal would show its own sentence, as every refusal does.
 *
 * `requireConsent` (R-pending-consent-gate): passed by the same gate for a signed-in account. With it, the server's
 * `legal.consentRequired` sends the owner to the terms page (`/account`, the assent page for an account) and back here,
 * and so does a CONSENT_REQUIRED from `create` or `spend`. Absent, nothing changes.
 */
export function Checkout({ onAccountGate, requireConsent = false }: {
  onAccountGate?: (code: AccountGateCode) => void;
  requireConsent?: boolean;
} = {}) {
  const session = useSession();
  const appealId = useAppealId();
  const navigate = useNavigate();
  const location = useLocation();
  const returned = (location.state as { returned?: boolean } | null)?.returned === true;
  // B6b (R189 decision 3) and launch day's option B (2026-10-14): which line says where credits work, or none where none
  // is true (`balanceLine.ts`).
  const creditsLine = balanceLine(useWebAccounts(), session.auth);

  const state = useCall(() => session.transport.v2Credits.get(), [session.transport, session.revision]);
  const offers = useCall(() => session.transport.v2CreateCheckout.offers(), [session.transport, session.revision]);
  const unlocked = state.status === "ok" ? state.response.appeal?.entitlement.unlocked === true : null;
  // R-pending-return-resolution: the server's switch, off the `get` above. Absent is off, and off asks nothing.
  const resumeOn = state.status === "ok" && state.response.body.capabilities.resolveResume === true;
  const resumes = unlocked === false && session.typedAddress === "" && resumeOn;
  const resolution = useCall(async (): Promise<V2Result<ResolvePropertyResponse | null>> => {
    if (unlocked !== false) return NO_ANSWER;
    if (session.typedAddress !== "") {
      return session.resolveAddress({ typedAddress: session.typedAddress, unit: unitOrNull(session.typedUnit) });
    }
    // THE TYPED ADDRESS IS GONE (a full-page return from Stripe, or a reload): the server's own stored answer, when it
    // offers one, within the eight seconds; a refusal, a throw or silence is no answer, which is today's page.
    if (!resumeOn) return NO_ANSWER;
    const answer = await within(
      Promise.resolve().then(() => session.transport.v2ResolveProperty.resume({ appealId })),
      PURCHASE_NOTICE_DEADLINE_MS,
    );
    return answer !== null && answer.ok ? answer : NO_ANSWER;
    // NOT on `session.revision`: a refresh re-reads the balance, and the address answer follows the balance's `unlocked`
    // — so the refresh after a spend cannot re-check an address the spend has just settled (409 APPEAL_LOCKED).
  }, [unlocked, resumeOn, appealId, session.transport, session.resolveAddress, session.typedAddress, session.typedUnit]);
  const resolved = resolution.status === "ok" ? resolution.response : null;
  // "Use a credit and start" waits while the server's answer is on its way, so the pop-up is never passed before its
  // protest-window notice arrives. Only while a resume is asked: the switch off, this is never true.
  const resuming = resumes && resolution.status === "pending";

  // R-pending-web-free-first: THE FREE CHECK'S FIRST LOOK, while the server says FREE_CHECK_V2_ENABLED is on
  // (`capabilities.freeCheckFirstLook`, from the `get` above) and the appeal is unspent. Absent is off, and off asks nothing
  // and draws nothing: the page is today's. Asked once per appeal in this tab (`app/freeFirst.ts`), once the address answer
  // has settled — the free check reads the appeal's address phase — within 45 seconds; any failure is no band.
  const firstLookOn = state.status === "ok" && state.response.body.capabilities.freeCheckFirstLook === true;
  const bandWanted = firstLookOn && unlocked === false;
  const asksFreeCheck = bandWanted && resolution.status !== "pending";
  const [freeCheck, setFreeCheck] = useState<{ appealId: string; answer: FreeCheckAnswer | null } | null>(null);
  const [freePanel, setFreePanel] = useState(false);
  const freeLink = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!asksFreeCheck) return;
    let live = true;
    void askFreeCheckOnce(session.transport, appealId).then((answer) => {
      if (live) setFreeCheck({ appealId, answer });
    });
    return () => {
      live = false;
    };
  }, [asksFreeCheck, appealId, session.transport]);

  const buy = useAction((productId: string) => session.transport.v2CreateCheckout.create({ productId }));
  // R-pending-ack-retention (P3, contract 2.31.0): the sentences the sheets showed, by hash (`app/shownCopy.ts`). The server
  // keeps them on the spend's record only with its SPEND_ACK_RECORDS_ENABLED on, and never refuses a spend over them.
  const spend = useAction(({ confirmed, shownCopy }: { confirmed: boolean; shownCopy: string[] }) =>
    session.transport.v2Credits.spend({
      appealId,
      ...(confirmed ? { confirmations: { address: true } } : {}),
      ...(shownCopy.length > 0 ? { shownCopy } : {}),
      // The build label the record names (web/<contract>+<commit>), as the assent records take it (Fable review P3-b).
      appVersion: APP_VERSION,
    }),
  );

  const [picked, setPicked] = useState<string | null>(null);
  const [sheet, setSheet] = useState<Sheet>("none");
  // "I meant this one" — the owner's own answer, kept for this visit so a second try is not asked again (AEF:52).
  const [confirmed, setConfirmed] = useState(false);
  // LAUNCH-PF: the server's notice the purchase sheet shows, and whether the page is asking for it.
  const [notice, setNotice] = useState<PurchaseNotice | null>(null);
  const [asking, setAsking] = useState(false);
  // R251: with a credit to spend the bundles stay folded until the owner asks for more.
  const [buyMore, setBuyMore] = useState(false);
  const sheetHeading = useRef<HTMLHeadingElement>(null);
  const buyCard = useRef<HTMLDivElement>(null);
  const startButton = useRef<HTMLButtonElement>(null);
  const bundlesId = useId();
  const buyCardId = useId();
  useEffect(() => {
    // The credit sheet is a pop-up over the page, which puts the keyboard on its own heading without moving the page
    // (`CreditPopUp`); the compass and notice sheets sit in the page, so the page brings them to the owner.
    if (sheet === "none" || sheet === "confirm") return;
    sheetHeading.current?.focus();
    sheetHeading.current?.scrollIntoView?.({ block: "center" });
  }, [sheet]);
  useEffect(() => {
    if (buyMore) bringToBuy(buyCard.current);
  }, [buyMore]);

  if (state.status === "pending") {
    return (
      <p className="spinner-line" role="status">
        Loading your account…
      </p>
    );
  }
  if (state.status === "failed") return <Refusal failure={state.failure} context="Your account" />;

  const body = state.response.body;
  // R-pending-consent-gate: an account without the current terms on file agrees to them first, then comes back here.
  if (requireConsent && body.legal.consentRequired === true) {
    const route: ConsentRoute = { from: "/checkout" };
    return <Navigate to="/account" replace state={route} />;
  }
  /** The server refused a purchase or a spend for the terms: to the terms page with its sentence, and back here. */
  const toTerms = (failure: V2Failure): boolean => {
    const copy = requireConsent ? consentRefusal(failure) : null;
    if (copy === null) return false;
    const route: ConsentRoute = { from: "/checkout", consentRefusal: copy };
    navigate("/account", { state: route });
    return true;
  };
  const paymentNotice = body.purchase.web.pendingPayment ?? body.purchase.web.paymentFailed;
  const buttons = checkoutButtonClasses(body.credits);
  const address = resolved === null ? null : spentOn(resolved);
  const g1 = resolved?.decisions["G-1"];
  const protest = resolved?.decisions["T-1"];
  const unitInForce = resolved?.body.address.unit?.value ?? null;
  const directional = resolved !== null && resolved.decisions["G-3"].ran && resolved.decisions["G-3"].reason === "directionalConflict";

  const purchase = async (productId: string) => {
    const created = await buy.run(productId);
    if (!created.ok) {
      if (toTerms(created)) return;
      const gate = accountGateCode(created);
      if (gate !== null && onAccountGate !== undefined) onAccountGate(gate);
      return;
    }
    const redirect = await session.checkout.redirectToStripe(created.response.body.redirectUrl);
    if (redirect.kind === "simulated") {
      session.refresh();
      navigate(redirect.returnTo);
    }
  };

  // THE SWITCH IS THE SERVER'S (`v2Credits` `get`, contract 2.28.0): absent is off, and off asks nothing.
  const prespendOn = body.capabilities.purchaseNoticePrespend === true;

  /** THE NEXT SHEET IN FRONT OF THE MONEY once the address is settled (`PurchaseNoticeGate.nextStep`): the notice while
   *  the server's stands unanswered, else the credit sheet — today's, with the switch off or no notice. */
  const afterAddress = async () => {
    // OFF IS TODAY, to the tick: the credit sheet, with nothing asked and nothing awaited.
    if (!prespendOn) {
      setSheet("confirm");
      return;
    }
    setAsking(true);
    // A LOOKUP THAT THROWS IS NO NOTICE, and never a button left disabled (LAUNCH-PF round 2): the app's
    // `try? await … lookup` reads any failure as nil, and so does this. MEASURED: a transport's own throw cannot reach
    // here today — the session's `watchAppealRefusals` makes every call async and `lookUpPurchaseNotice` reads a
    // rejection as no notice — so this is the belt, held by `test/purchaseNoticeBelt.test.tsx`, which makes the lookup
    // itself throw.
    let found: PurchaseNotice | null;
    try {
      found = await lookUpPurchaseNotice(session.transport, prespendOn, appealId, address, unitInForce);
    } catch {
      found = null;
    } finally {
      setAsking(false);
    }
    setNotice(found);
    setSheet(nextPreSpendSheet(found, heldPurchaseAnswer(appealId)));
  };

  const start = () => {
    const hold = resolved !== null && holdsForAddress(resolved) && !confirmed;
    if (hold) {
      setSheet("compass");
      return;
    }
    void afterAddress();
  };

  /** The owner's choice on the notice. "Go back" closes it and spends nothing; a route goes on to the credit sheet. */
  const choose = (choice: PurchaseNoticeChoice) => {
    const answer = recordPurchaseChoice(appealId, choice);
    setSheet(answer === null ? "none" : "confirm");
  };

  // "Get credits", with a credit in hand (R251): the app's words for opening its purchase sheet
  // (AEF:808). Here it unfolds the buy card on this page, brings it into view and puts the keyboard on its Buy button (or
  // on the card, while the offers are still loading).
  const toBuy = () => {
    if (buyMore) bringToBuy(buyCard.current);
    else setBuyMore(true);
  };

  const useCredit = async () => {
    // LAUNCH-PF, BELT-AND-BRACES: an unanswered notice stands in front of the spend, whatever route reached this function.
    // The notice is told before the money, never after it.
    const held = heldPurchaseNotice(appealId);
    if (held !== null && !spendAllowed(held.notice, held.answer)) {
      setNotice(held.notice);
      setSheet("purchase");
      return;
    }
    const facts = {
      address, unit: unitInForce, directional, compassConfirmed: confirmed, purchaseAnswer: held?.answer?.answer ?? null,
      protestNotice: protest !== undefined && protest.ran && protest.copy !== null ? protest.copy.text : null,
    };
    const shownCopy = await hashesOf([...spendSheetTexts(facts), ...spendServedTexts(facts)]);
    const result = await spend.run({ confirmed, shownCopy });
    if (result.ok && result.response.body.spend.ok) {
      session.refresh();
      navigate("/details");
      return;
    }
    if (!result.ok && toTerms(result)) return;
    // THE SERVER HELD IT FOR THE ADDRESS after all (its answer is what counts, not this page's): the compass sheet, when
    // this page can state the address back; otherwise the refusal's own sentence stays, with the way to fix it.
    if (!result.ok && heldForAddress(result) && !confirmed && address !== null) setSheet("compass");
  };

  const bundles = offers.status === "ok" ? offers.response.body.products.filter((p) => !p.returningOwner) : [];
  const returning = offers.status === "ok" ? offers.response.body.products.find((p) => p.returningOwner) ?? null : null;
  // The app opens its picker on the larger bundle (`selectedBundle = .three`): the one that mints the most credits.
  const defaultBundle = bundles.reduce<CheckoutProduct | null>((best, p) => (best === null || p.credits > best.credits ? p : best), null);
  const selected = bundles.find((p) => p.productId === picked) ?? defaultBundle;
  const canBuy = offers.status === "ok" && offers.response.body.available && buy.state.status !== "pending";
  const spendFailure = spend.state.status === "failed" ? spend.state.failure : null;
  const heldWithoutAddress = spendFailure !== null && heldForAddress(spendFailure) && address === null;

  const hasCredit = body.credits > 0;
  // NO CREDIT: the buy card is the page's next thing to do, always shown. A CREDIT TO SPEND: folded until "Get credits".
  const showBuyCard = !hasCredit || buyMore;

  // THE FREE CHECK'S BAND: the placeholder while it is asked, the first look once answered, nothing for no answer, a
  // refusal or a screen with nothing to promote. Its pop-up's buttons into the paid flow close it and do this page's own
  // next thing — to the Buy button with no credit, "Use a credit and start" with one — once the pop-up has gone, so the
  // keyboard lands there and not back on the link. Never Stripe from the pop-up.
  const answered = freeCheck !== null && freeCheck.appealId === appealId ? freeCheck.answer : undefined;
  const look = answered === undefined ? null : answered?.firstLook ?? null;
  const freeScreen = answered?.screen ?? null;
  const band = !bandWanted || (answered !== undefined && (look === null || freeScreen === null))
    ? null
    : <FreeFirstBand look={look} onOpen={() => setFreePanel(true)} linkRef={freeLink} />;
  const proceedFromFreeCheck = () => {
    setFreePanel(false);
    setTimeout(() => {
      if (!hasCredit) {
        bringToBuy(buyCard.current);
        return;
      }
      // THE CREDIT SHEET RECORDS WHERE THE KEYBOARD IS WHEN IT OPENS and gives it back there on "Not yet" (`PopUp`). The free
      // check's pop-up has just handed the keyboard back to the band's link, so without this the credit sheet would record
      // the link and "Not yet" would land on it; on the normal path the keyboard is on "Use a credit and start", and so it is here.
      startButton.current?.focus({ preventScroll: true });
      start();
    }, 0);
  };

  return (
    <>
      <p className="eyebrow">Before you pay</p>
      <PrePayPanel address={address} unit={unitInForce} compact={hasCredit} band={band} />
      {freePanel && band !== null && freeScreen !== null && (
        <FreeCheckPanel
          screen={freeScreen}
          onClose={() => setFreePanel(false)}
          onProceed={proceedFromFreeCheck}
          returnTo={freeLink}
        />
      )}

      {/* A DELAYED PAYMENT STILL ON ITS WAY, OR ONE THAT FAILED (LAUNCH-PAY rounds 4 and 6; Tyler approved both sentences
          2026-10-07): the server's sentence, when it sends one, in place of "Thanks for your purchase… in a moment", which
          would be false — and on any visit, so nobody pays twice or waits for credits that are not coming. The server sends
          at most one (pending wins), and this page shows at most one, pending first, whatever it is sent. */}
      {paymentNotice !== null && (
        <div className="note returned" data-payment-notice={paymentNotice.code}>
          <CopyLine copy={paymentNotice} />
        </div>
      )}
      {/* "…will appear here in a moment" ONLY WHILE NO CREDIT IS ON THE ACCOUNT (Tyler, launch day, 2026-10-14: his
          screenshot showed it beside "Credits on your account 1", after the credit had come). Once the balance holds a
          credit, the line would be stale, so the balance says it instead. */}
      {returned && paymentNotice === null && !hasCredit && (
        <div className="note good returned">
          <HeldLine id="checkout.returned" />
          <button className="btn ghost" type="button" onClick={() => session.refresh()}>
            Check again
          </button>
        </div>
      )}

      {/* The staging bypass says so here, where the owner is about to pay (G-1 `info`). */}
      {g1 !== undefined && g1.ran && g1.outcome === "info" && <DecisionView decision={g1} what="Whether we serve this state" />}

      {/* WITH A CREDIT TO SPEND: the balance, and beside it the next thing to do — the sheets open here, where the owner
          pressed, and the credit sheet over the page. "Use a credit and start" stays under the pop-up, so the keyboard has
          somewhere to come back to. "Get credits" unfolds the bundles for an owner who wants more. With no credit there is
          no such card: the balance is the buy card's header line (R251). */}
      {hasCredit && (
        <div className="pay start" data-has-credit={hasCredit}>
          <div className="kv">
            <span>Credits on your account</span>
            <span data-credits={body.credits}>{body.credits}</span>
          </div>
          {(sheet === "none" || sheet === "confirm") && (
            <>
              {sheet === "none" && spendFailure !== null && <Refusal failure={spendFailure} context="Using a credit" />}
              <Actions>
                <button
                  ref={startButton}
                  className={buttons.start}
                  type="button"
                  onClick={start}
                  disabled={asking || resuming}
                  aria-busy={asking || resuming}
                >
                  Use a credit and start
                </button>
                {sheet === "none" && heldWithoutAddress && (
                  <button className="btn ghost" type="button" data-app-copy="compass.fix" onClick={() => navigate("/")}>
                    {prePayText("compass.fix")}
                  </button>
                )}
                <button
                  className={buttons.buy}
                  type="button"
                  data-app-copy="entry.getCredits"
                  data-buy-more
                  aria-expanded={showBuyCard}
                  aria-controls={showBuyCard ? buyCardId : undefined}
                  onClick={toBuy}
                >
                  {prePayText("entry.getCredits")}
                </button>
              </Actions>
            </>
          )}
        </div>
      )}

      {sheet === "compass" && address !== null && (
        <section className="sheet" aria-labelledby="compass-title" data-sheet="compass">
          <h2 id="compass-title" ref={sheetHeading} tabIndex={-1} data-app-copy="compass.title">
            {prePayText("compass.title")}
          </h2>
          <p className="copy muted" data-app-copy="compass.body">{prePayText("compass.body")}</p>
          <p className="sheet-address" data-address={address}>{address}</p>
          <p className="copy muted" data-app-copy="compass.different">{prePayText("compass.different")}</p>
          <Actions>
            <button className="btn primary" type="button" data-app-copy="compass.fix" onClick={() => navigate("/")}>
              {prePayText("compass.fix")}
            </button>
            <button
              className="btn quiet"
              type="button"
              data-app-copy="compass.anyway"
              disabled={asking}
              aria-busy={asking}
              onClick={() => {
                // THE ONLY ROUTE TO THE SPEND FROM HERE, and the only thing on the website that says "I meant this one".
                // LAUNCH-PF: the address is settled now, so the next sheet is the app's ordering's (`afterAddress`).
                setConfirmed(true);
                void afterAddress();
              }}
            >
              {prePayTemplate("compass.anyway", { address })}
            </button>
          </Actions>
        </section>
      )}

      {sheet === "purchase" && notice !== null && (
        <PurchaseNoticeSheet notice={notice} onChoice={choose} headingRef={sheetHeading} />
      )}

      {sheet === "confirm" && (
        <PopUp
          labelledBy="confirm-title"
          kind="confirm"
          heading={sheetHeading}
          onEscape={spend.state.status === "pending" ? null : () => setSheet("none")}
          returnTo={startButton}
        >
          <div className="popup-head">
            <div className="popup-head-text">
              <h2 id="confirm-title" ref={sheetHeading} tabIndex={-1} data-app-copy="confirm.title">
                {prePayText("confirm.title")}
              </h2>
              <p className="copy muted" data-app-copy={address === null ? "confirm.coversNoAddress" : "confirm.covers"}>
                {address === null ? prePayText("confirm.coversNoAddress") : prePayTemplate("confirm.covers", { address })}
              </p>
            </div>
            {/* The panel's letter page, small (Tyler, 2026-10-13: "i enjoyed the document on it"). A drawing with no
                words, hidden from readers, as the panel's is. */}
            <figure className="letter-preview popup-letter" aria-hidden="true" data-letter-preview>
              <LetterIllustration />
            </figure>
          </div>
          {unitInForce !== null && (
            <p className="sheet-line" data-app-copy="confirm.unit">{prePayTemplate("confirm.unit", { unit: unitInForce })}</p>
          )}
          {directional && <p className="sheet-line warn" data-app-copy="confirm.directional">{prePayText("confirm.directional")}</p>}
          {/* The protest window, before the charge (AEF:497-521): T-1's notice, which the server states. */}
          {protest !== undefined && protest.ran && protest.copy !== null && (
            <div className="sheet-note">
              <CopyLine copy={protest.copy} />
            </div>
          )}
          {/* M3 (AEF:481, approved for both clients 2026-09-30): when the credit is used, said at the moment it is. */}
          <p className="popup-m3" data-app-copy="confirm.m3">{prePayText("confirm.m3")}</p>
          {spendFailure !== null && <Refusal failure={spendFailure} context="Using a credit" />}
          {spend.state.status === "ok" && (
            <>
              <DecisionView decision={spend.state.response.decisions["G-1"]} what="Whether we serve this state" />
              <DecisionView decision={spend.state.response.decisions["G-3"]} what="Whether the address needs confirming" />
              <DecisionView decision={spend.state.response.decisions["G-4"]} what="Whether we have what the appeal needs" />
              <DecisionView decision={spend.state.response.decisions["G-5"]} what="Whether we handle this property type" />
            </>
          )}
          <Actions>
            <button
              className="btn primary"
              type="button"
              disabled={spend.state.status === "pending"}
              data-app-copy={spend.state.status === "pending" ? "confirm.using" : "confirm.use"}
              onClick={() => void useCredit()}
            >
              {prePayText(spend.state.status === "pending" ? "confirm.using" : "confirm.use")}
            </button>
            <button
              className="btn quiet"
              type="button"
              disabled={spend.state.status === "pending"}
              data-app-copy="confirm.notYet"
              onClick={() => setSheet("none")}
            >
              {prePayText("confirm.notYet")}
            </button>
            {heldWithoutAddress && (
              <button className="btn ghost" type="button" data-app-copy="compass.fix" onClick={() => navigate("/")}>
                {prePayText("compass.fix")}
              </button>
            )}
          </Actions>
        </PopUp>
      )}

      {/* The server lists the returning-owner product only for an account R102 says may have it (`offeredProducts`), which
          is the app's condition too (`ReturningOffer.showsOffer`, with the product loaded). Above the buy card, so an owner
          who may have the lower price sees it before the bundles' Buy button. */}
      {returning !== null && (
        <section className="card returning" data-product-id={returning.productId}>
          <h2 data-app-copy="purchase.returning.title">{prePayText("purchase.returning.title")}</h2>
          <p className="copy" data-app-copy="purchase.returning.body">{prePayText("purchase.returning.body")}</p>
          <button
            className={buttons.buy}
            type="button"
            disabled={!canBuy}
            data-app-copy="purchase.returning.buy"
            data-price-cents={returning.unitAmountCents}
            onClick={() => void purchase(returning.productId)}
          >
            {prePayTemplate("purchase.returning.buy", { price: returning.displayPrice })}
          </button>
        </section>
      )}

      {/* THE BUY CARD (R251): "Choose a bundle" with the balance as a small line in its header (no
          credit only — with one, the balance card above says it), the test-mode line, the bundles and the one Buy button;
          under it two of Tyler's approved lines, small, each with a symbol; the can-raise line; then the fine print. The
          test-mode line, the can-raise line and the fine print are drawn whatever the balance: the card an owner with a
          credit unfolds with "Get credits" is this same card, with the same notices before the money
          (R-pending-review-fixes). */}
      {showBuyCard && (
        <div className="card buy-card" id={buyCardId} data-web-checkout={offers.status} data-buy-card ref={buyCard} tabIndex={-1}>
          <div className="buy-card-head">
            <h3 id={bundlesId} data-app-copy="purchase.bundles.title">{prePayText("purchase.bundles.title")}</h3>
            {!hasCredit && (
              <p className="buy-balance" data-buy-balance>
                <span>Credits on your account</span>
                <span data-credits={body.credits}>{body.credits}</span>
              </p>
            )}
          </div>
          {offers.status === "pending" && (
            <p className="spinner-line" role="status">
              Loading what you can buy…
            </p>
          )}
          {offers.status === "failed" && <Refusal failure={offers.failure} context="Buying a credit" />}
          {offers.status === "ok" && (
            <>
              {offers.response.body.mode === "test" && <HeldLine id="checkout.testMode" className="muted" />}
              {offers.response.body.unavailable !== null && <CopyNote copy={offers.response.body.unavailable} tone="stop" />}
              <div className="bundles" role="radiogroup" aria-labelledby={bundlesId}>
                {bundles.map((product) => {
                  const on = selected?.productId === product.productId;
                  return (
                    <label key={product.productId} className={on ? "bundle on" : "bundle"} data-product-id={product.productId}>
                      <input
                        type="radio"
                        name="bundle"
                        value={product.productId}
                        checked={on}
                        onChange={() => setPicked(product.productId)}
                      />
                      <span className="bundle-label">{bundleLabel(product)}</span>
                      <span className="bundle-price" data-price-cents={product.unitAmountCents}>{product.displayPrice}</span>
                    </label>
                  );
                })}
              </div>
              {selected !== null && (
                <>
                  <Actions>
                    <button
                      className={`${buttons.buy} block`}
                      type="button"
                      disabled={!canBuy}
                      data-app-copy="purchase.buy"
                      data-buy={selected.productId}
                      onClick={() => void purchase(selected.productId)}
                    >
                      {prePayTemplate("purchase.buy", { label: bundleLabel(selected), price: selected.displayPrice })}
                    </button>
                  </Actions>
                  <ul className="buy-assurances" data-buy-assurances>
                    <li data-held-copy="checkout.benefits.neverExpire">
                      <span className="buy-assurance-icon" aria-hidden="true">
                        <CalendarIcon size={16} />
                      </span>
                      <span>{heldText("checkout.benefits.neverExpire")}</span>
                    </li>
                    <li data-held-copy="checkout.benefits.straightAnswer">
                      <span className="buy-assurance-icon" aria-hidden="true">
                        <Icon name="seal" size={16} />
                      </span>
                      <span>{heldText("checkout.benefits.straightAnswer")}</span>
                    </li>
                  </ul>
                </>
              )}
            </>
          )}
          {buy.state.status === "failed" && <Refusal failure={buy.state.failure} context="Buying a credit" />}
          {/* THE CAN-RAISE NOTICE, ONE CALM LINE, before the fine print (R251: Tyler's "for raise.. B", 2026-10-13),
              WHENEVER THIS CARD IS SHOWN, whatever the balance (R-pending-review-fixes): an owner with a credit who
              unfolds the card to buy more is told before paying, as the app's purchase sheet tells every buyer
              (BuyPacketsSheet.swift `backfireCard`). With the card folded there is no line, and nothing to pay. The
              owner still confirms it at the Generate step, which shows the server's whole card beside its checkbox. */}
          {resolved !== null && (
            <CanRaiseLine canRaise={resolved.body.canRaise} state={resolved.body.jurisdiction.state} />
          )}
          {/* Decision 2: the assent page's disclosure row, small, by the Buy button — the same approved text. */}
          <FinePrint className="fineprint by-buy" />
        </div>
      )}

      {/* WEB-COPY-1 item 8 is true of a balance the app cannot reach — a look-around's, or a password account's without
          Apple. With the server's WEB_ACCOUNTS_ENABLED on, an account is told its credits are saved to it, and how the app
          reaches them: by Apple, linked or to add (option B, 2026-10-14). Where none is true, or the switch is not yet
          heard, no line (B6b, `balanceLine.ts`). */}
      {creditsLine !== null && <HeldLine id={creditsLine} className="muted" />}
    </>
  );
}

/**
 * `/checkout/return` — where Stripe sends the owner after paying (the server's fixed success route,
 * `functions/src/v2/checkout/returnOrigin.ts`). THE APP GOES STRAIGHT BACK TO WHERE THE OWNER WAS (CR-1;
 * BuyPacketsSheet.swift:591-601), so this route does too: to `/checkout`, carrying the approved thanks line (item 18),
 * where the balance is read from the server and can be read again. Credits arrive by Stripe's webhook, which may land a
 * moment after the owner does; nothing here assumes the payment succeeded or adds a credit.
 *
 * THE OWNER ARRIVES ON A FRESH PAGE (WEB-APPEAL-ID): Stripe's return is a full load, so the typed address is gone and the
 * appeal id is what this tab kept before it left (`app/appealId.ts`). `/checkout`'s spend names that appeal — or, if the
 * tab kept none, `/checkout` sends the owner to `/` with the credit waiting.
 */
export function CheckoutReturn() {
  return <Navigate to="/checkout" replace state={{ returned: true }} />;
}
